/**
 * @genesis_domain Feedback & Error States
 * @genesis_version V9.1
 * @genesis_intent Global React Error Boundary with V65 Dual-Glass Protocol styling for uncaught React errors.
 * @genesis_boundaries This component is FORBIDDEN from containing business logic, API calls, or route-specific behavior.
 * @genesis_ai_trail V77.0: Enhanced as part of GENESIS V77 Safety Net Audit. Implements V65 Dual-Glass Protocol with Surface Anchor gradient for optical grounding.
 */

import React, { Component, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can log the error to an error reporting service here
    console.error('React Error Boundary caught an error:', error);
    console.error('Error details:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[var(--color-primary-base)]/5 to-[var(--color-bg-base)]">
          {/* Surface Anchor Layer - Prevents "washed out" look */}
          <div className="absolute inset-0 z-[-1] bg-gradient-to-b from-transparent to-[var(--color-primary-base)]/10 pointer-events-none" />

          <div className="relative w-full max-w-md">
            {/* Mobile: Studio Equilibrium (Dark glass, inverted text) */}
            <div className="md:hidden rounded-2xl theme-card backdrop-blur-3xl border border-[var(--color-glass-border)] shadow-[0_0_25px_var(--color-primary-base)/40%,inset_0_1px_0_rgba(255,255,255,0.6)] p-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[var(--color-danger-subtle)] flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-[var(--color-danger-base)]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>

                <h2 className="text-2xl font-bold text-[var(--color-card-text)] mb-3 font-sans tracking-tight">
                  Something went wrong
                </h2>

                <p className="text-[var(--color-content-secondary)] mb-6 font-sans">
                  We're sorry, but an unexpected error occurred. Please try refreshing the page.
                </p>

                {this.state.error && (
                  <div className="w-full mb-6 p-4 bg-[var(--color-panel-overlay)] rounded-lg text-left">
                    <p className="text-sm font-medium text-[var(--color-card-text)] mb-1 font-sans">Error details:</p>
                    <code className="text-xs text-[var(--color-content-secondary)] font-mono break-words">
                      {this.state.error.message}
                    </code>
                  </div>
                )}

                <div className="flex flex-col w-full gap-3">
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full px-6 py-3 bg-[var(--color-primary-base)]/40 text-[var(--color-content-inverse)] font-medium rounded-lg hover:bg-[var(--color-primary-base)]/50 transition-all duration-200 active:scale-95 font-sans h-14 min-h-[56px] flex items-center justify-center"
                  >
                    Refresh Page
                  </button>

                  <button
                    onClick={() => (window.location.href = '/')}
                    className="w-full px-6 py-3 bg-[var(--color-surface-elevated)] text-[var(--color-card-text)] font-medium rounded-lg hover:bg-[var(--color-surface-hover)] transition-all duration-200 active:scale-95 font-sans h-14 min-h-[56px] flex items-center justify-center"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop: Luminous Frost (Light glass, chromatic inversion) */}
            <div className="hidden md:block rounded-2xl theme-card backdrop-blur-3xl border border-[var(--color-glass-border)] shadow-[0_20px_50px_rgba(79,70,229,0.15)] p-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[var(--color-danger-subtle)] flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-[var(--color-danger-base)]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>

                <h2 className="text-2xl font-bold text-[var(--color-card-text)] mb-3 font-sans tracking-tight">
                  Something went wrong
                </h2>

                <p className="text-[var(--color-content-secondary)] mb-6 font-sans">
                  We're sorry, but an unexpected error occurred. Please try refreshing the page.
                </p>

                {this.state.error && (
                  <div className="w-full mb-6 p-4 bg-[var(--color-panel-overlay)] rounded-lg text-left">
                    <p className="text-sm font-medium text-[var(--color-card-text)] mb-1 font-sans">Error details:</p>
                    <code className="text-xs text-[var(--color-content-tertiary)] font-mono break-words">
                      {this.state.error.message}
                    </code>
                  </div>
                )}

                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => window.location.reload()}
                    className="flex-1 px-6 py-3 bg-[var(--color-primary-base)] text-[var(--color-content-inverse)] font-medium rounded-lg hover:bg-[var(--color-primary-hover)] transition-all duration-200 hover:shadow-md hover:ring-[var(--color-primary-base)]/20 font-sans h-14 min-h-[56px] flex items-center justify-center"
                  >
                    Refresh Page
                  </button>

                  <button
                    onClick={() => (window.location.href = '/')}
                    className="flex-1 px-6 py-3 bg-[var(--color-surface-elevated)] text-[var(--color-content-secondary)] font-medium rounded-lg hover:bg-[var(--color-surface-hover)] transition-all duration-200 hover:shadow-md font-sans h-14 min-h-[56px] flex items-center justify-center"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}